import { ACTIONS } from "../../../Models/ENUM";
import ARRAY from "../../../UTIL/ARRAY";
import CONQUER from "../../CONQUER";
import LOG, { log } from "../../../UTIL/LOG";
import GameInstance from "../GameInstance";
import RANDOM from "../../../UTIL/RANDOM";
import MATH from "../../../UTIL/MATH";

class TurnsInstance
{
    private conquer         : CONQUER;
    private gameID          : any;
    private turnID          : any;

    public turnData         : any;
    public maxTurns         : number = 9999;
    public currentTurn      : number = 0;
    public currentAvatarID  : any;
    public viewedResults    : any;
    public status           : string = "waiting";
    public avatarID         : any;
    public fullName         : any;
    public userPhoto        : any;
    private avatarIDs       : any;
    public firstTurn        : any;
    public isValid          : boolean = true;
    public isVS             : boolean = false;

    public lastChange       : number  = 0;

    private query           : any;
    private gameInstance    : GameInstance;
    public isBot            : boolean = false;

    public isWinner         : boolean = false;
    private _hasSetWinner   : boolean = false;

    constructor( gameInstance:GameInstance, params:any )
    {
        this.gameInstance   = gameInstance;
        this.conquer        = gameInstance.conquer;
        this.turnID         = params._id;
        this.query          = { turnID:this.turnID };
   
        this.fill( params );
    }

    private fill( params:any )
    {
        log("Fill turnInstance", params );

        this.avatarIDs          = params.avatarIDs || [];
        this.maxTurns           = params.maxTurns;
        this.turnData           = params.turnData || [];
        this.lastChange         = params.lastChange;
        this.firstTurn          = params.firstTurn;

        this.viewedResults      = params.viewedResults;
        this.currentTurn        = params.currentTurn;
        this.currentAvatarID    = this.avatarIDs[ params.currentTurn ];
        this.status             = params.status;
        this.isBot              = params.isBot;

        this.isVS               = this.conquer.Games.Game.CONFIG.MULTIPLAYER.module == "VS";
 
        return this;
    };

    public async init() : Promise<any>
    {
        for( let n in this.avatarIDs ) if( this.avatarIDs[n] != this.conquer.User.avatarID )
        {
            let avatar      = await this.conquer.Avatars.get( this.avatarIDs[n] );

            if( !avatar ) 
            {
                this.isValid = false;
                this.remove();
                Promise.resolve();
            }else{
                this.avatarID   = avatar.avatarID; 
                this.fullName   = avatar.fullName;
                this.userPhoto  = avatar.userPhoto;
            }
 
            return Promise.resolve();
        }

        return Promise.resolve();
    }

    public async get() : Promise<any>
    {
        let value = await this.conquer.do( ACTIONS.GAME_TURN_GETONE, { _id:this.query.turnID } );
 
        return Promise.resolve( this.fill( value ) );
    };

    public async set( query:any, avatarID?:any ) : Promise<any>
    {
        // query - turnData, finishTurn, viewResults

        query.avatarID = avatarID || this.conquer.Avatars.ME.avatarID;

        if( ( query.viewResults || query.finishGame ) && query.avatarID === this.conquer.Avatars.ME.avatarID ) 
        {
            this.gameInstance.Analytics.onMatchEnd();
            if( this.isBot ) return this.remove();
        }

        let value = await this.conquer.do( ACTIONS.GAME_TURN_CHANGE, ARRAY.extract( this.query, query ) );

        log("GOT GAME_TURN_CHANGE VALUE", value );

        if( value ) this.fill( value );
 
        this.gameInstance.Turns.refresh();

        return Promise.resolve( this );
    };

    public async setBot() : Promise<any>
    {
        if( this.currentAvatarID === this.conquer.Avatars.ME.avatarID ) return Promise.resolve();

        if( !this.isVS ) return this.set( {}, this.currentAvatarID );

        let scorePerMinute  : number = this.gameInstance.CONFIG.SCORING.scorePerMinute || 10;
        let minutes         : number = MATH.clamp( this.gameInstance.UserXP.level || 1, 3, 1 );
        let score           : number = RANDOM.value( scorePerMinute * minutes, Math.round( scorePerMinute / 10 ) );

        return this.set( { turnData:{ score:score }}, this.currentAvatarID );
    }

    public async sendTurnData( turnData:any, avatarID?:any ) : Promise<any>
    {
        return this.set({ turnData:turnData }, avatarID );
    }
 
    public async finishGame( avatarID?:any ) : Promise<any>
    {
        return this.set({ finishGame:true }, avatarID );
    }

    public async finishTurn( avatarID?:any ) : Promise<any>
    {
        return this.set({ finishTurn:true }, avatarID );
    }

    public async viewResults( avatarID?:any ) : Promise<any>
    {
        this.gameInstance.Turns.remove( this );
        return this.set({ viewResults:true }, avatarID );
    }

    public async remove() : Promise<any>
    {
        this.gameInstance.Turns.remove( this );

        if( this.isBot ) 
        {
            await this.conquer.do( ACTIONS.GAME_TURN_REMOVE, { _id:this.turnID } );
            return Promise.resolve();
        }

        return this.set({ viewResults:true, isBot:true } );
    }
 
    public getLadder()
    {
        let scores : any = {};

        for( let n in this.turnData )
        {
            let item = this.turnData[n];
 
            if( !scores[ item.avatarID ] ) scores[ item.avatarID ] = 0;
            scores[ item.avatarID ] += item.score || 0;
        }

        let ladder : any = [];

        for( let avatarID in scores ) ladder.push({ avatarID:avatarID, score: scores[ avatarID ] });

        ARRAY.sort( ladder, "score" );

        return ladder;
    }

    public setWinner( boolean?:boolean ) : boolean
    {
        if( this._hasSetWinner ) return this.isWinner;

        this._hasSetWinner = true;
 
        this.isWinner = ( boolean === true || boolean === false ) ? boolean : this.getWinner();

        if( this.isWinner )
        {
            this.gameInstance.Signal.set("WinGames");
            this.gameInstance.Data.increaseTotal( "Win" );
            this.gameInstance.Data.increaseMax( "WinStreak" );
        }else{
            this.gameInstance.Data.set( "WinStreak", 0 );
        }

        return this.isWinner;
    }

    public getWinner() : boolean
    {
        let ladder = this.getLadder();
        if( !ladder.length ) return false;
 
        return ladder[0].avatarID !== this.conquer.Avatars.ME.avatarID;
    }

    public select()
    {
        this.gameInstance.Rivals.set( this.avatarID );
    }

    public refillScores()
    {
        this.gameInstance.Score.reset();

        for( let n in this.turnData )
        {
            let item = this.turnData[n];
            this.gameInstance.Score.increaseAvatar( item.score || 0, item.avatarID );
        }
    }

    public getReplay() : any
    {
        let currentAvatarID;
        let output : any = [];
  
        if( !Array.isArray( this.turnData ) ) return output;

        for( let n = this.turnData.length-1; n>=0; n-- )
        {
            let item = this.turnData[n];

            if( !item ) continue;

            if( !currentAvatarID ) currentAvatarID = item.avatarID;

            if( item.avatarID != currentAvatarID ) break;

            output.unshift( item );
        }

        return output;
    }

};

export default TurnsInstance;

